/**
 * ⚠️ **`site.base` a été RETIRÉE, et rien ne doit la rétablir.** Elle valait `/`,
 * une adresse qui n'affiche plus rien : elle aiguille un compte connecté vers son
 * espace et fait sortir un anonyme vers la landing. Un écran qui veut « ramener à
 * l'accueil » ne vise donc plus un chemin de ce fichier — l'accueil vit hors de
 * l'application, et son adresse se lit dans `LandingHelper`.
 */
export const RouterPath = {
  auth: {
    base: '/auth',
    register: '/auth/register',
    login: '/auth/login',
    emailSent: '/auth/email-sent',
    awaitingVerification: '/auth/awaiting-verification',
    forgotPassword: '/auth/forgot-password',
    resetPassword: '/auth/reset-password',
  },
  admin: {
    base: '/admin',
    home: (id: string) => `/admin/${id}`,
    dashboard: (id: string) => `/admin/${id}/dashboard`,
    partnership: (id: string) => `/admin/${id}/partnership`,
    /**
     * ⚠️ **Deux surfaces, pas une page qui déplie tout.** La performance et les
     * prélèvements répondent tous deux à « que s'est-il passé sur ce
     * partenariat », et méritent chacun la place de le montrer — le relevé était
     * déplié dans la carte de la liste, sous des boutons qui n'ont rien à voir.
     */
    /**
     * ⚠️ Le relevé du portefeuille a sa propre surface : la liste grandit sans
     * borne, et elle était dépliée dans une carte montée dans la liste des
     * partenariats.
     */
    wallet: (id: string) => `/admin/${id}/wallet`,
    /**
     * ⚠️ **Une surface à part, parce que le formulaire est un objet du COMPTE.**
     * Le ranger dans l'écran de promotion en aurait fait un champ du composant —
     * or il se réutilise d'un composant à l'autre, et ses réponses lui survivent.
     */
    /**
     * ⚠️ **La boîte de réception, pas la gestion.** Ce que le compte vient
     * chercher ici est ce qu'il a REÇU ; ses formulaires sont l'outil qui l'a
     * produit, et ils vivent une porte plus loin.
     */
    form: (id: string) => `/admin/${id}/form`,
    formManage: (id: string) => `/admin/${id}/form/manage`,
    /**
     * ⚠️ **Regarder ne doit pas coûter le risque d'écrire.** Sans cette adresse,
     * le seul écran qui montrait les questions était l'éditeur — qui publie une
     * version de plus dès qu'on valide.
     */
    formDetail: (id: string, formId: string) => `/admin/${id}/form/manage/${formId}`,
    /**
     * ⚠️ **`new` avant `[formId]`** — Next résout le segment statique en
     * premier, donc aucun formulaire ne peut être masqué par cette adresse.
     */
    formNew: (id: string) => `/admin/${id}/form/manage/new`,
    formEdit: (id: string, formId: string) => `/admin/${id}/form/manage/${formId}/edit`,
    /**
     * ⚠️ **Le chemin de RETOUR.** Le message mène au composant ; sans celui-ci,
     * la navigation n'irait que dans un sens, et le filtre par composant servi
     * par l'API n'aurait aucun déclencheur.
     */
    formForComponent: (id: string, componentId: string) => `/admin/${id}/form?componentId=${componentId}`,
    partnershipStats: (id: string, partnershipId: string) => `/admin/${id}/partnership/stats/${partnershipId}`,
    partnershipTransactions: (id: string, partnershipId: string) => `/admin/${id}/partnership/transactions/${partnershipId}`,
    profile: (id: string) => `/admin/${id}/profile`,
    /** Les critères vivent dans les PARAMÈTRES : c'est un réglage du compte, pas un écran de travail. */
    criteria: (profileId: string) => `/admin/${profileId}/settings/criteria`,
    accountFunnel:(profileId:string) => `/admin/${profileId}/account-verification`,
    accountVerification:(profileId:string) => `/admin/${profileId}/account-verification?tab=0`,
    paymentMethod:(profileId:string) => `/admin/${profileId}/settings/payment-method`,
    /**
     * Le relevé d'une sponsorisation.
     *
     * ⚠️ **Sous `promotion`, pas sous `campaign`** : côté écran on ne parle plus
     * de campagne mais d'angle, « campagne est du jargon d'un monde où
     * l'utilisateur n'est pas encore ». L'onglet Publicité, lui, n'existe plus :
     * tout ce qu'il savait faire, créer, suspendre, reprendre, arrêter,
     * recharger et lire les chiffres, se fait depuis l'angle. L'identifiant
     * reste celui de la campagne : c'est elle qui porte l'argent.
     */
    /**
     * Le détail d'un angle sponsorisé.
     *
     * ⚠️ **Indexé sur l'ANGLE, pas sur la campagne.** L'utilisateur ne voit
     * qu'une sponsorisation — « campagne est du jargon d'un monde où
     * l'utilisateur n'est pas encore » — alors qu'un angle en accumule plusieurs
     * au fil du temps. Clé sur la campagne, l'URL changeait à chaque nouvelle
     * sponsorisation et l'historique devenait inatteignable.
     *
     * ⚠️ **La PÉRIODE regardée vit en query param.** C'est ce qui rend la vue
     * extensible : toute sous-vue ajoutée ici lira la même sélection, sans
     * qu'aucune n'ait à la porter.
     */
    angleDetail: (id: string, angleId: string) => `/admin/${id}/promotion/detail/${angleId}`,
    promotion: (id: string) => `/admin/${id}/promotion`,
    promotionDetail: (id: string, componentId: string) => `/admin/${id}/promotion/${componentId}`,
    domain: (id: string) => `/admin/${id}/domain`,
    settings: (id: string) => `/admin/${id}/settings`,
    settingsCategory: (id: string) => `/admin/${id}/settings/category`,
    settingsPaymentMethod: (id: string) => `/admin/${id}/settings/payment-method`,
    settingsPlan: (id: string) => `/admin/${id}/settings/plan`,
    management: {
      categories: (id: string) => `/admin/${id}/management/categories`,
      categoryDetail: (id: string, categoryId: string) => `/admin/${id}/management/categories/${categoryId}`,
      reindex: (id: string) => `/admin/${id}/management/reindex`,
      disputes: (id: string) => `/admin/${id}/management/disputes`,
      sanctions: (id: string) => `/admin/${id}/management/sanctions`,
      capability: (id: string) => `/admin/${id}/management/capability`,
    },
  },
  profile : {
    slug: (slug:string) => `/${slug}`,
    preview: (slug:string) =>  `/${slug}?preview=1`,
  },
};